// src/pages/CalculoHonorarios/hooks/useHonorarios.js

import { useState, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook customizado para gerenciar estado e operações de honorários
 */
const useHonorarios = () => {
  const [honorarios, setHonorarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [gerandoEmail, setGerandoEmail] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Carregar lista de honorários
  const carregarHonorarios = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/honorarios');

      if (response.data.success) {
        setHonorarios(response.data.data);
      } else {
        setError(response.data.message || 'Erro ao carregar honorários');
      }
    } catch (err) {
      console.error('Erro ao carregar honorários:', err);
      setError('Erro ao carregar honorários: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar honorário por ID
  const buscarHonorarioPorId = async (id) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`/api/honorarios/${id}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao buscar honorário');
        return null;
      }
    } catch (err) {
      console.error('Erro ao buscar honorário:', err);
      setError('Erro ao buscar honorário: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Criar novo honorário
  const criarHonorario = async (dados) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/honorarios', dados);

      if (response.data.success) {
        setSuccess('Honorário criado com sucesso!');
        await carregarHonorarios();
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao criar honorário');
        return null;
      }
    } catch (err) {
      console.error('Erro ao criar honorário:', err);
      setError('Erro ao criar honorário: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar honorário
  const atualizarHonorario = async (id, dados) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(`/api/honorarios/${id}`, dados);

      if (response.data.success) {
        setSuccess('Honorário atualizado com sucesso!');
        await carregarHonorarios();
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao atualizar honorário');
        return null;
      }
    } catch (err) {
      console.error('Erro ao atualizar honorário:', err);
      setError('Erro ao atualizar honorário: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Excluir honorário
  const excluirHonorario = async (id, descricao) => {
    if (!window.confirm(`Tem certeza que deseja excluir o honorário?\n\n"${descricao.substring(0, 100)}..."`)) {
      return false;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(`/api/honorarios/${id}`);

      if (response.data.success) {
        setSuccess('Honorário excluído com sucesso!');
        await carregarHonorarios();
        return true;
      } else {
        setError(response.data.message || 'Erro ao excluir honorário');
        return false;
      }
    } catch (err) {
      console.error('Erro ao excluir honorário:', err);
      setError('Erro ao excluir honorário: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Calcular honorários (LLM1 → LLM2)
  const calcularHonorarios = async (id) => {
    setCalculando(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`/api/honorarios/${id}/calcular`);

      if (response.data.success) {
        setSuccess('Cálculo realizado com sucesso!');
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao calcular honorários');
        return null;
      }
    } catch (err) {
      console.error('Erro ao calcular honorários:', err);
      setError('Erro ao calcular honorários: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setCalculando(false);
    }
  };

  // Gerar email de proposta (LLM3)
  const gerarEmailProposta = async (id, alteracoes = null) => {
    setGerandoEmail(true);
    setError('');
    setSuccess('');

    try {
      // ✅ NOVO: Enviar alterações no body se fornecidas
      const body = alteracoes ? { alteracoes } : {};
      
      const response = await axios.post(`/api/honorarios/${id}/gerar-email`, body);

      if (response.data.success) {
        setSuccess('Email gerado com sucesso!');
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao gerar email');
        return null;
      }
    } catch (err) {
      console.error('Erro ao gerar email:', err);
      setError('Erro ao gerar email: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setGerandoEmail(false);
    }
  };


  // Buscar último cálculo
  const buscarUltimoCalculo = async (id) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`/api/honorarios/${id}/calculo`);

      if (response.data.success) {
        return response.data.data;
      } else {
        setError(response.data.message || 'Erro ao buscar cálculo');
        return null;
      }
    } catch (err) {
      console.error('Erro ao buscar cálculo:', err);
      setError('Erro ao buscar cálculo: ' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Limpar mensagens
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return {
    honorarios,
    loading,
    calculando,
    gerandoEmail,
    error,
    success,
    carregarHonorarios,
    buscarHonorarioPorId,
    criarHonorario,
    atualizarHonorario,
    excluirHonorario,
    calcularHonorarios,
    gerarEmailProposta,
    buscarUltimoCalculo,
    clearMessages
  };
};

export default useHonorarios;